/**
 * Mercado do bot (espelha pix_config.currency_mode e src/utils/currency.py):
 * Brasil (BRL, R$), Global (USD, $) e LATAM (código 'mxn': valores na
 * moeda-base escolhida, MXN por padrão; cada comprador paga convertido na
 * moeda do país dele). Fonte única para rótulos de preço — não repita
 * `mode === 'usd' ? … : …`. No LATAM passe também a moeda-base (`base`).
 */
export const MARKETS = {
    brl: { code: "BRL", symbol: "R$", label: "Brasil" },
    usd: { code: "USD", symbol: "$", label: "Global" },
    mxn: { code: "MXN", symbol: "MX$", label: "LATAM" },
};

/**
 * Moedas em que os valores do bot LATAM podem ser escritos (espelha
 * LATAM_BASE_CURRENCIES no backend). `min` = valor mínimo de um plano.
 */
export const LATAM_BASES = {
    MXN: { code: "MXN", symbol: "MX$", decimals: 2, min: 20, flag: "mx" },
    USD: { code: "USD", symbol: "$", decimals: 2, min: 2, flag: "us" },
    COP: { code: "COP", symbol: "COL$", decimals: 0, min: 10000, flag: "co" },
    CLP: { code: "CLP", symbol: "CLP$", decimals: 0, min: 1000, flag: "cl" },
};

export const normalizeLatamBase = (base) => {
    const key = String(base || "").toUpperCase();
    return LATAM_BASES[key] ? key : "MXN";
};

/** Países do LATAM, na ordem do backend (src/services/payment/local_methods.py). `flag` = código do flagcdn. */
export const LATAM_COUNTRIES = [
    { currency: "MXN", name: "México", flag: "mx" },
    { currency: "COP", name: "Colombia", flag: "co" },
    { currency: "CLP", name: "Chile", flag: "cl" },
];

/** Só as moedas de países do LATAM, sem repetir, na ordem dos países. */
export const inLatamOrder = (currencies) =>
    LATAM_COUNTRIES.map((c) => c.currency).filter((c) => (currencies || []).includes(c));

export const normalizeMode = (mode) => {
    const key = String(mode || "").toLowerCase();
    return MARKETS[key] ? key : "brl";
};

/** { code, symbol, label, decimals, min } dos valores do bot. */
export function marketOf(mode, base) {
    const key = normalizeMode(mode);
    if (key !== "mxn") return { ...MARKETS[key], decimals: 2, min: 2 };
    const latam = LATAM_BASES[normalizeLatamBase(base)];
    return { ...MARKETS.mxn, code: latam.code, symbol: latam.symbol, decimals: latam.decimals, min: latam.min };
}

/** Moeda em que os valores do bot estão escritos ('BRL', 'USD', 'MXN'...). */
export const baseCurrencyOf = (mode, base) => marketOf(mode, base).code;

export function formatMoney(mode, value, base) {
    const { symbol, decimals } = marketOf(mode, base);
    const n = parseFloat(value);
    return !value || Number.isNaN(n) ? `${symbol} --` : `${symbol} ${n.toFixed(decimals)}`;
}

export const currencyLabelOf = (mode, base) => `(${marketOf(mode, base).symbol})`;

/** Valor mínimo de um plano/oferta: no LATAM o da moeda-base (MX$ 20...), 2 nos demais. */
export const minPlanValue = (mode, base) => marketOf(mode, base).min;

/** Mensagem de erro se algum valor está abaixo do mínimo do mercado; senão null. */
export function planValueError(values, mode, base) {
    const { min, symbol, decimals } = marketOf(mode, base);
    if (!(values || []).some((v) => Number(v) < min)) return null;
    const minText = min.toFixed(decimals ? 2 : 0).replace(".", ",");
    return `O valor mínimo de cada plano é ${symbol} ${minText} e não pode ser negativo.`;
}
